import { useState } from "react";
import "./EinrichtungSmart.scss";
import { transformPayload } from "../../Constants";
import { sendToAzureFunction, getFormData, deleteToken } from "../../services/ApiService";
import { handleFormSubmit } from "../../services/FormService";
import storePhoto from "../../resources/storePhoto.png";

interface EinrichtungSmartProps {
  onSubmitCallback: (status: string) => void;
  tokenID: string;
  confirmedEmail: string;
  processID: number;
}

const EinrichtungSmart: React.FC<EinrichtungSmartProps> = ({ onSubmitCallback, tokenID, confirmedEmail, processID }) => {
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [passwort, setPasswort] = useState("");
  const [confirmPasswort, setConfirmPasswort] = useState("");
  const [securePin, setSecurePin] = useState("");
  const [confirmSecurePin, setConfirmSecurePin] = useState("");
  const [koerpergroesse, setKoerpergroesse] = useState("");
  const [koerpergewicht, setKoerpergewicht] = useState("");
  const [zugangsdaten, setZugangsdaten] = useState("");
  const [sonstiges, setSonstiges] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [hasConsent, setHasConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleCancel = async () => {
    try {
      await deleteToken(tokenID!);
      // After deletion, reload to trigger token validation and show error message
      window.location.reload();
    } catch (error) {
      // Optionally, show an error if deletion failed
      alert("Fehler beim Abbrechen des Vorgangs. Bitte versuchen Sie es erneut.");
    }
  };

  // Validate phone number
  const validatePhoneNumber = (value: string) => {
    const regex = /^\+?[0-9\s\-]{9,20}$/;
    if (hasConsent && !value) {
      setPhoneNumberError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (hasConsent && !regex.test(value)) {
      setPhoneNumberError("Ungültige Telefonnummer.");
      return false;
    } else {
      setPhoneNumberError("");
      return true;
    }
  };

  // Validate Secure PIN
  const validateSecurePin = (value: string) => {
    const pinRegex = /^\d{4}$/;
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, securePin: "Dieses Feld darf nicht leer sein." }));
      return false;
    } else if (!pinRegex.test(value)) {
      setErrors(prev => ({ ...prev, securePin: "Bitte geben Sie eine 4-stellige PIN ein." }));
      return false;
    } else if (confirmSecurePin && value !== confirmSecurePin) {
      setErrors(prev => ({ ...prev, confirmSecurePin: "Die PINs stimmen nicht überein." }));
      return false;
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.securePin;
        return newErrors;
      });
      return true;
    }
  };

  // Validate Confirm Secure PIN
  const validateConfirmSecurePin = (value: string) => {
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, confirmSecurePin: "Dieses Feld darf nicht leer sein." }));
      return false;
    } else if (value !== securePin) {
      setErrors(prev => ({ ...prev, confirmSecurePin: "Die PINs stimmen nicht überein." }));
      return false;
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.confirmSecurePin;
        return newErrors;
      });
      return true;
    }
  };

  // Validate the form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validate Vorname
    if (!vorname.trim()) {
      newErrors.vorname = "Vorname ist erforderlich.";
    }

    // Validate Nachname
    if (!nachname.trim()) {
      newErrors.nachname = "Nachname ist erforderlich.";
    }

    // Validate Email
    if (!email.trim()) {
      newErrors.email = "E-Mail ist erforderlich.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Ungültige E-Mail-Adresse.";
    }

    // Validate Confirm Email
    if (!confirmEmail.trim()) {
      newErrors.confirmEmail = "E-Mail bestätigen ist erforderlich.";
    } else if (confirmEmail !== email) {
      newErrors.confirmEmail = "E-Mails stimmen nicht überein.";
    }

    // Validate Passwort
    if (!passwort.trim()) {
      newErrors.passwort = "Passwort ist erforderlich.";
    }

    // Validate Confirm Passwort
    if (!confirmPasswort.trim()) {
      newErrors.confirmPasswort = "Passwort bestätigen ist erforderlich.";
    } else if (confirmPasswort !== passwort) {
      newErrors.confirmPasswort = "Passwörter stimmen nicht überein.";
    }

    // Validate Secure PIN
    if (!validateSecurePin(securePin)) {
      Object.assign(newErrors, errors);
    }

    // Validate Confirm Secure PIN
    if (!validateConfirmSecurePin(confirmSecurePin)) {
      Object.assign(newErrors, errors);
    }

    // Validate Sonstiges
    if (!sonstiges.trim()) {
      newErrors.sonstiges = "Dieses Feld darf nicht leer sein.";
    }

    setErrors(newErrors);

    // Validate Phone Number if consent is given
    if (hasConsent && !validatePhoneNumber(phoneNumber)) {
      return false;
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    handleFormSubmit(
      e,
      validateForm,
      getFormData,
      transformPayload,
      sendToAzureFunction,
      setIsSubmitted,
      onSubmitCallback,
      tokenID,
      confirmedEmail,
      processID
    );
  };

  return (
    <div className="email-form">
      <h2 style={{ marginTop: 0 }}>Einrichtung Smartwatch</h2>
      <img
        src={storePhoto}
        alt="Store Photo"
        style={{ width: "100%", height: "auto", maxWidth: "100%", borderRadius: "10px", marginBottom: "20px" }}
      />

      <p style={{width:"87%", border: "1px solid black", backgroundColor: "#f0f0f0", padding: "15px", borderRadius: "5px", textAlign: "center", marginBottom: "20px" }}>
        Bitte geben Sie Ihre Daten für die Durchführung des Services ein
      </p>

      <form onSubmit={handleSubmit}>
        {/* Vorname */}
        <div className="form-group">
          <label>Vorname</label>
          <input
            type="text"
            value={vorname}
            name="vorname"
            onChange={(e) => setVorname(e.target.value)}
            required
          />
          {isSubmitted && errors.vorname && (
            <small className="error-message">{errors.vorname}</small>
          )}
        </div>

        {/* Nachname */}
        <div className="form-group">
          <label>Nachname</label>
          <input
            type="text"
            value={nachname}
            name="nachname"
            onChange={(e) => setNachname(e.target.value)}
            required
          />
          {isSubmitted && errors.nachname && (
            <small className="error-message">{errors.nachname}</small>
          )}
        </div>

        {/* Email */}
        <div className="form-group">
          <label>E-Mailadresse</label>
          <input
            type="email"
            value={email}
            name="email"
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {isSubmitted && errors.email && (
            <small className="error-message">{errors.email}</small>
          )}
          <small>Bei jeder Smartwatch ist ein Benutzerkonto erforderlich, dafür die E-Mailadresse</small>
        </div>

        {/* Confirm Email */}
        <div className="form-group">
          <label>E-Mailadresse bestätigen</label>
          <input
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            required
          />
          {isSubmitted && errors.confirmEmail && (
            <small className="error-message">{errors.confirmEmail}</small>
          )}
        </div>

        {/* Passwort */}
        <div className="form-group">
          <label>Passwort</label>
          <input
            type="password"
            value={passwort}
            name="password"
            onChange={(e) => setPasswort(e.target.value)}
            required
          />
          {isSubmitted && errors.passwort && (
            <small className="error-message">{errors.passwort}</small>
          )}
          <small>Passwort für das Benutzerkonto</small>
        </div>

        {/* Confirm Passwort */}
        <div className="form-group">
          <label>Passwort bestätigen</label>
          <input
            type="password"
            value={confirmPasswort}
            onChange={(e) => setConfirmPasswort(e.target.value)}
            required
          />
          {isSubmitted && errors.confirmPasswort && (
            <small className="error-message">{errors.confirmPasswort}</small>
          )}
        </div>

        {/* Körpergröße (Optional) */}
        <div className="form-group">
          <label>Körpergröße(cm) (Optional)</label>
          <input
            type="text"
            value={koerpergroesse}
            name="koerpergroesse"
            onChange={(e) => setKoerpergroesse(e.target.value)}
          />
          <small>Optional, für die Fitnessfunktionen der Uhr</small>
        </div>

        {/* Körpergewicht (Optional) */}
        <div className="form-group">
          <label>Körpergewicht(kg) (Optional)</label>
          <input
            type="text"
            value={koerpergewicht}
            name="koerpergewicht"
            onChange={(e) => setKoerpergewicht(e.target.value)}
          />
          <small>Optional, für die Fitnessfunktionen der Uhr</small>
        </div>

        {/* Zugangsdaten (Optional) */}
        <div className="form-group">
          <label>Zugangsdaten zum Onlinekonto des Mobifunkanbiters (Optional)</label>
          <input
            type="text"
            value={zugangsdaten}
            name="zugangsdaten"
            onChange={(e) => setZugangsdaten(e.target.value)}
          />
        </div>

        {/* Sonstiges */}
        <div className="form-group">
          <label>Sonstiges / Kundenwunsch</label>
          <textarea
            value={sonstiges}
            name="sonstiges"
            onChange={(e) => setSonstiges(e.target.value)}
            rows={3}
            required
          ></textarea>
          {isSubmitted && errors.sonstiges && (
            <small className="error-message">{errors.sonstiges}</small>
          )}
        </div>

        {/* Secure PIN Fields */}
        <br />
        <div style={{ borderTop: "1px solid lightgrey" }}></div>
        <br />

        <div className="form-group">
          <label>Neue PIN</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={securePin}
            name="SecurePIN"
            onChange={(e) => {
              setSecurePin(e.target.value);
              validateSecurePin(e.target.value);
              if (confirmSecurePin) validateConfirmSecurePin(confirmSecurePin);
            }}
            required
          />
          <small>Bitte legen Sie eine sichere 4-stellige PIN fest. Sie benötigen diese PIN für den Zugriff auf Ihre Falldetails und können von einem Mitarbeiter des Geschäfts zur Überprüfung abgefragt werden. Behandeln Sie die PIN vertraulich und geben Sie sie nicht an Dritte weiter.</small>
          {isSubmitted && errors.securePin && (
            <small className="error-message">{errors.securePin}</small>
          )}
        </div>

        <div className="form-group">
          <label>Neue PIN bestätigen</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={confirmSecurePin}
            onChange={(e) => {
              setConfirmSecurePin(e.target.value);
              validateConfirmSecurePin(e.target.value);
            }}
            required
          />
          <small>Bitte bestätigen Sie Ihre 4-stellige PIN.</small>
          {isSubmitted && errors.confirmSecurePin && (
            <small className="error-message">{errors.confirmSecurePin}</small>
          )}
        </div>

        <br />
        <div style={{ borderTop: "1px solid lightgrey" }}></div>
        <br />

        {/* Consent Checkbox */}
        <div className="form-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={hasConsent}
              onChange={(e) => {
                setHasConsent(e.target.checked);
                if (!e.target.checked) {
                  setPhoneNumber("");
                  setPhoneNumberError("");
                }
              }}
            />
            <span className="checkmark"></span>
            Ich stimme zu, dass ich für Angelegenheiten im Zusammenhang mit dieser Anfrage kontaktiert werden darf.
          </label>
        </div>

        {/* Phone Number */}
        {hasConsent && (
          <div className="form-group">
            <label>Telefonnummer</label>
            <input
              type="tel"
              value={phoneNumber}
              name="HandyNummer"
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                validatePhoneNumber(e.target.value);
              }}
              required={hasConsent}
            />
            <small>Bitte geben Sie eine gültige Telefonnummer ein.</small>
            {isSubmitted && phoneNumberError && (
              <small className="error-message">{phoneNumberError}</small>
            )}
          </div>
        )}

        <button type="submit">Absenden</button>
        <button type="button" onClick={handleCancel} style={{ backgroundColor: "red", marginBottom: 0 }}>
          Abbrechen
        </button>
        <small style={{color: "#777"}}>Durch das Abbrechen wird der Vorgang gestoppt und Ihre Daten werden nicht weiter verwendet</small>
      </form>
    </div>
  );
};

export default EinrichtungSmart;